import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { motion } from "motion/react"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { CinemaAction } from "../../types/entities/action"
import { normalizeCinemaActions, sanitizeActionHtml } from "../../lib/afishaActions"
import BannerAdDisclosure, { hasBannerAdInfo } from "../../features/afisha/BannerAdDisclosure"
import { afishaHref } from "../../lib/afishaTabs"
import { applyDocumentSeo, excerpt, SITE_NAME } from "../../lib/siteSeo"
import { cinemaHref } from "../../lib/cinemaSlugs"
import { useCinemaSlug } from "../../hooks/useCinemaHref"
import { actionPath, actionSlug, findActionBySlug, parseActionIdFromSlug } from "../../lib/actionSlug"
import { AfishaBanner } from "../../types/entities/banner"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export default function Promo() {
    const { promoSlug } = useParams<{ promoSlug: string }>()
    const navigate = useNavigate()
    const cinemaSlug = useCinemaSlug()
    const { cinema } = appStore()
    const { requestData } = useRequest()
    const [action, setAction] = useState<CinemaAction | null>(null)
    const [actions, setActions] = useState<CinemaAction[]>([])
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)

    useEffect(() => {
        let cancelled = false
        if (!cinema?.id || !promoSlug) {
            setNotFound(true)
            setLoading(false)
            return
        }

        const load = async () => {
            setLoading(true)
            setNotFound(false)

            const listRes = await requestData<CinemaAction[]>({
                type: "GET",
                path: `cinema/${cinema.id}/actions?channel=site`,
                disableError: true,
            })
            if (cancelled) return

            const list = listRes.status === "success" ? normalizeCinemaActions(listRes.data) : []
            setActions(list)

            let found = findActionBySlug(list, promoSlug)
            const actionId = found?.id ?? parseActionIdFromSlug(promoSlug)

            // В списке обычно нет HTML-тела — его отдаёт только GET /actions/:id
            if (actionId) {
                const oneRes = await requestData<CinemaAction>({
                    type: "GET",
                    path: `cinema/${cinema.id}/actions/${actionId}?channel=site`,
                    disableError: true,
                })
                if (cancelled) return
                if (oneRes.status === "success" && oneRes.data && typeof oneRes.data === "object" && oneRes.data.id) {
                    found = oneRes.data
                }
            }

            if (!found) {
                setAction(null)
                setNotFound(true)
                setLoading(false)
                return
            }

            const peers = list.length ? list : [found]
            const canonical = actionSlug(found, peers)
            if (canonical !== promoSlug) {
                navigate(cinemaHref(cinemaSlug, actionPath(found, peers)), { replace: true })
                return
            }

            setAction(found)
            setLoading(false)
        }

        load()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinema?.id, promoSlug, cinemaSlug])

    useEffect(() => {
        if (!action) return
        const fromBody = (action.body ?? "").replace(/<[^>]+>/g, " ")
        const description =
            excerpt(action.subtitle) ||
            excerpt(fromBody) ||
            `Акция «${action.title}» в сети «Мягкий кинотеатр».`
        applyDocumentSeo({
            title: `${action.title} — ${SITE_NAME}`,
            description,
            path: cinemaHref(cinemaSlug, actionPath(action, actions)),
            image: action.image,
        })
    }, [action, actions, cinemaSlug])

    if (loading) {
        return (
            <div className="flex flex-col gap-4 p-5">
                <div className="aspect-[16/9] w-full animate-pulse rounded-[20px] bg-white/[0.06]" />
                <div className="h-7 w-2/3 animate-pulse rounded bg-white/[0.06]" />
                <div className="h-24 w-full animate-pulse rounded bg-white/[0.06]" />
            </div>
        )
    }

    if (notFound || !action) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 px-5 py-24 text-white/40">
                <span className="text-[64px]">🎟</span>
                <p style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}>Акция не найдена</p>
                <Link
                    to={afishaHref("Акции")}
                    className="text-[14px] text-[#349be6] underline decoration-[#349be6]/40 underline-offset-4"
                    style={{ fontFamily: BODY, fontWeight: 600 }}>
                    К списку акций
                </Link>
            </div>
        )
    }

    const banner: AfishaBanner = {
        id: action.id,
        image: action.image,
        title: action.title,
        erid: action.erid ?? undefined,
        legal_entity_inn: action.legal_entity_inn ?? undefined,
    }
    const html = sanitizeActionHtml(action.body ?? "")

    return (
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-5 p-5">
            <div className="relative overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.04]">
                <div className="relative aspect-[16/9] w-full">
                    <img src={action.image} alt={action.title} className="size-full object-cover" />
                </div>
                {hasBannerAdInfo(banner) && (
                    <div className="absolute right-2 top-2 z-10 md:right-3 md:top-3">
                        <BannerAdDisclosure banner={banner} />
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <p
                    className="text-[10px] uppercase tracking-[0.08em] text-white/40"
                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                    Акция
                </p>
                <h1
                    className="text-white leading-tight"
                    style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24, letterSpacing: "-0.02em" }}>
                    {action.title}
                </h1>
                {action.subtitle?.trim() ? (
                    <p
                        className="text-[15px] leading-snug text-white/60"
                        style={{ fontFamily: BODY, fontWeight: 500 }}>
                        {action.subtitle.trim()}
                    </p>
                ) : null}
            </div>

            {html ? (
                <div
                    className="action-body text-[15px] leading-relaxed text-white/90 [&_a]:font-semibold [&_a]:text-[#349be6] [&_a]:underline [&_b]:font-extrabold [&_b]:text-white [&_strong]:font-extrabold [&_strong]:text-white [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc"
                    style={{ fontFamily: BODY, fontWeight: 600 }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ) : null}

            <Link
                to={afishaHref("Акции")}
                className="mt-2 self-start text-[13px] text-white/50 transition-colors hover:text-white/80"
                style={{ fontFamily: BODY, fontWeight: 600 }}>
                ← Все акции
            </Link>
        </motion.article>
    )
}
